import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import {
    applySessionCampaignNameToLeadFormUrl,
    BASE_CAMPAIGN_NAME,
    GENERAL_LEAD_UTM_MAPPING,
    getUtmTermFromUrl,
    isPersistedMediaCampaignName,
    readMediaKmpSession,
    resolveMappedCampaignName,
    type UtmMappingEntry,
    writeMediaKmpSession,
} from '@hyundai/shared-lib';
import { filter } from 'rxjs';
import { PLATFORM_TOKEN, Platform } from '../providers/platform.token';

@Injectable({
    providedIn: 'root',
})
export class CampaignTrackingService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly platform = inject(PLATFORM_TOKEN, { optional: true });
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    private readonly campaignNameState = signal(BASE_CAMPAIGN_NAME);
    readonly campaignName = this.campaignNameState.asReadonly();

    private readonly enabled = isPlatformBrowser(this.platformId) && this.platform === Platform.HWS;

    constructor() {
        if (!this.enabled) {
            return;
        }

        this.restoreFromSession();
        this.captureFromCurrentUrl();

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.captureFromCurrentUrl());
    }

    /**
     * Resolves campaignName for the current page: URL mappings (CMS then General-Lead),
     * otherwise the session value, otherwise the base name.
     */
    peekCampaignName(utmTerm: string | undefined, pageMappings: UtmMappingEntry[] = []): string {
        const fromUrl = this.resolveFromMappings(utmTerm, pageMappings);
        if (fromUrl) {
            return fromUrl;
        }
        return this.campaignNameState();
    }

    /**
     * Persists a valid Media-KMP mapping for the browser session when utm_term matches
     * page/CMS mappings or General-Lead mappings.
     */
    captureIfMapped(utmTerm: string | undefined, pageMappings: UtmMappingEntry[] = []): void {
        if (!this.enabled) {
            return;
        }

        const campaignName = this.resolveFromMappings(utmTerm, pageMappings);
        const normalizedUtmTerm = utmTerm?.trim();
        if (!campaignName || !normalizedUtmTerm) {
            return;
        }

        this.persist(normalizedUtmTerm, campaignName);
    }

    applyToLeadFormUrl(url: string): string {
        if (!this.enabled || !isPersistedMediaCampaignName(this.campaignNameState())) {
            return url;
        }
        return applySessionCampaignNameToLeadFormUrl(url, this.campaignNameState());
    }

    private captureFromCurrentUrl(): void {
        this.captureIfMapped(getUtmTermFromUrl(this.router.url));
    }

    private restoreFromSession(): void {
        const storage = this.getSessionStorage();
        if (!storage) {
            return;
        }

        const stored = readMediaKmpSession(storage);
        if (stored) {
            this.campaignNameState.set(stored.campaignName);
        }
    }

    private resolveFromMappings(utmTerm: string | undefined, pageMappings: UtmMappingEntry[]): string | undefined {
        return (
            resolveMappedCampaignName(utmTerm, pageMappings) ??
            resolveMappedCampaignName(utmTerm, GENERAL_LEAD_UTM_MAPPING)
        );
    }

    private persist(utmTerm: string, campaignName: string): void {
        const storage = this.getSessionStorage();
        if (storage) {
            writeMediaKmpSession(storage, { utmTerm, campaignName });
        }
        this.campaignNameState.set(campaignName);
    }

    private getSessionStorage(): Storage | undefined {
        try {
            return sessionStorage;
        } catch {
            return undefined;
        }
    }
}
